import React, { useState, useEffect, useRef } from 'react';
import AutoTranslateText from '../components/AutoTranslateText';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import GoogleMap from '../components/GoogleMap';
import { useGoogleMapsApiKey } from '../hooks/useGoogleMapsApiKey';
import { apiKeyService } from '../services/apiKeyService';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { loadGoogleMapsApi } from '../utils/googleMapsLoader';
import { ALL_COUNTRIES } from '../utils/countries';

// Déclarations de types pour Google Maps API
declare global {
  interface Window {
    google: any;
  }
}

// Types pour l'API Google Maps
interface GoogleMapsGeocoder {
  geocode(request: { address: string }, callback: (results: any[], status: string) => void): void;
}

interface GoogleMapsLocation {
  lat(): number;
  lng(): number;
}

// Liste complète des pays avec leurs drapeaux
const countries = ALL_COUNTRIES.map((country) => ({
  code: country.code,
  name: country.label,
  flag: country.flag
}));

// Configuration par défaut du plugin
const defaultGeolocationConfig = {
  title: 'Ma localisation',
  address: '',
  city: '',
  postalCode: '',
  country: 'FR',
  latitude: null,
  longitude: null,
  mapSize: 'medium',
  customWidth: 500,
  customHeight: 300,
  responsive: true, // ✅ par défaut
  zoom: 15,
  mapType: 'roadmap',
  showMarker: true,
  // Nouvelles propriétés pour compatibilité
  additionalMarkers: [],
  enableStreetView: false,
  showDirectionsButton: false,
  directionsButtonText: 'Obtenir un itinéraire',
  enableZoomControl: true,
  enableMapTypeControl: false,
  enableFullscreenControl: false,
  markerColor: '#FF0000',
  markerIcon: 'default',
  customMarkerUrl: '',
  borderStyle: 'none',
  
  // ✅ Nouveaux champs pour le style
  textColor: '#333333',
  borderRadius: 0,
  userId: '',

  // Anciennes propriétés conservées pour compatibilité
  multiMarkers: false,
  markers: [],
  showRoute: false,
  routeMode: 'driving',
  showDistance: false,
  searchEnabled: false,
  showUserDirections: false, // ✅ nouveau
  directionsMode: 'driving', // ✅ nouveau
};

// Composant de configuration
const GeolocationConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange, t }) => {
  const { apiKey, isLoading, error, hasApiKey } = useGoogleMapsApiKey();

  const [isGeocoding, setIsGeocoding] = useState(false); // ✅ ajouté
  const [geocodeError, setGeocodeError] = useState<string | null>(null); // ✅ ajouté

  const updateConfig = (updates: any) => {
    onConfigChange({ ...config, ...updates });
  };

  // ✅ sauvegarde l'ID utilisateur du createur (une seule fois)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || config.userId) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      if (ownerId) {
        updateConfig({ userId: String(ownerId) });
      }
    } catch (e) {
      console.warn("Impossible de recuperer l'ID utilisateur pour ce plugin.");
    }
  }, []);

  // Fonction de géocodage pour convertir l'adresse en coordonnées
  const geocodeAddress = async () => {
    if (!apiKey) {
      setGeocodeError('Clé API Google Maps non disponible');
      return;
    }

    await loadGoogleMapsScript(apiKey);

    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      setGeocodeError('API Google Maps non chargée');
      return;
    }

    const fullAddress = [config.address, config.city, config.postalCode, countries.find(c => c.code === config.country)?.name]
      .filter(Boolean)
      .join(', ');

    if (!fullAddress.trim()) {
      setGeocodeError('Veuillez saisir au moins une partie de l\'adresse');
      return;
    }

    setIsGeocoding(true);
    setGeocodeError(null);

    try {
      const geocoder: GoogleMapsGeocoder = new window.google.maps.Geocoder();
      const result = await new Promise<any[]>((resolve, reject) => {
        geocoder.geocode({ address: fullAddress }, (results: any[], status: string) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Géocodage échoué: ${status}`));
          }
        });
      });

      const location: GoogleMapsLocation = result[0].geometry.location;
      updateConfig({
        latitude: location.lat(),
        longitude: location.lng()
      });
      
      setGeocodeError(null);
    } catch (error: any) {
      setGeocodeError(`Impossible de trouver cette adresse: ${error.message}`);
    } finally {
      setIsGeocoding(false);
    }
  };

  // Auto-géocodage quand l'adresse change (avec délai)
  useEffect(() => {
    if (!config.address && !config.city) return;
    
    const timeoutId = setTimeout(() => {
      if (apiKey && (config.address || config.city)) {
        geocodeAddress();
      }
    }, 1500); // Délai de 1.5 secondes après la dernière modification

    return () => clearTimeout(timeoutId);
  }, [config.address, config.city, config.postalCode, config.country, apiKey]);

  // Afficher un message d'erreur si pas de clé API
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden"><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></span>
        </div>
      </div>
    );
  }

  if (error || !hasApiKey) {
    return (
      <div className="alert alert-warning" role="alert">
        <h6 className="alert-heading">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Clé API Google Maps requise
        </h6>
        <p className="mb-2">
          Pour utiliser le plugin de géolocalisation, vous devez configurer une clé API Google Maps dans votre profil.
        </p>
        <hr />
        <div className="mb-0">
          <strong><AutoTranslateText sourceLang="fr">Instructions :</AutoTranslateText></strong>
          <ol className="mb-2">
            <li><AutoTranslateText sourceLang="fr">Allez dans votre profil utilisateur</AutoTranslateText></li>
            <li><AutoTranslateText sourceLang="fr">Section "Clés API"</AutoTranslateText></li>
            <li><AutoTranslateText sourceLang="fr">Ajoutez une clé API Google Maps</AutoTranslateText></li>
            <li><AutoTranslateText sourceLang="fr">Revenez ici pour utiliser le plugin</AutoTranslateText></li>
          </ol>
          <p className="mb-0">
            <small className="text-muted">
              Vous pouvez obtenir une clé API Google Maps gratuitement sur la 
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">
                Console Google Cloud
              </a>.
            </small>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Titre de la carte</AutoTranslateText></label>
        <input
          type="text"
          className="form-control"
          value={config.title || ''}
          onChange={(e) => updateConfig({ title: e.target.value })}
          placeholder="Ex: Notre localisation"
        />
      </div>
      
      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Adresse complète</AutoTranslateText></label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={config.address || ''}
            onChange={(e) => updateConfig({ address: e.target.value })}
            placeholder="Ex: 123 Rue de la Paix"
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={geocodeAddress}
            disabled={isGeocoding || !apiKey}
            title="Rechercher les coordonnées automatiquement"
          >
            {isGeocoding ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fas fa-search"></i>
            )}
          </button>
        </div>
        {geocodeError && (
          <div className="text-danger small mt-1">
            <i className="fas fa-exclamation-triangle me-1"></i>
            {geocodeError}
          </div>
        )}
        <small className="text-muted">
          Les coordonnées seront automatiquement calculées à partir de l'adresse
        </small>
      </div>

      <div className="row">
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Ville</AutoTranslateText></label>
          <input
            type="text"
            className="form-control"
            value={config.city || ''}
            onChange={(e) => updateConfig({ city: e.target.value })}
            placeholder="Ex: Paris"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Code postal</AutoTranslateText></label>
          <input
            type="text"
            className="form-control"
            value={config.postalCode || ''}
            onChange={(e) => updateConfig({ postalCode: e.target.value })}
            placeholder="Ex: 75001"
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Pays</AutoTranslateText></label>
        <select
          className="form-select"
          value={config.country || 'FR'}
          onChange={(e) => updateConfig({ country: e.target.value })}
        >
          {countries.map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
      </div>
      
      <div className="row">
        <div className="col-md-6">
          <label className="form-label">
            Latitude 
            {isGeocoding && <span className="spinner-border spinner-border-sm ms-2" role="status"></span>}
          </label>
          <input
            type="number"
            step="0.000001"
            className="form-control"
            value={config.latitude || ''}
            onChange={(e) => updateConfig({ latitude: parseFloat(e.target.value) })}
            placeholder="Ex: 48.8566"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">
            Longitude
            {isGeocoding && <span className="spinner-border spinner-border-sm ms-2" role="status"></span>}
          </label>
          <input
            type="number"
            step="0.000001"
            className="form-control"
            value={config.longitude || ''}
            onChange={(e) => updateConfig({ longitude: parseFloat(e.target.value) })}
            placeholder="Ex: 2.3522"
          />
        </div>
      </div>
      
      <div className="mb-3">
        <small className="text-info">
          <i className="fas fa-info-circle me-1"></i>
          Les coordonnées sont automatiquement calculées à partir de l'adresse. 
          Vous pouvez aussi les saisir manuellement pour plus de précision.
        </small>
      </div>

      <div className="mb-3">
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm btn-outline-info"
            onClick={() => {
              updateConfig({
                latitude: 48.8566,
                longitude: 2.3522,
                address: 'Champs-Élysées',
                city: 'Paris',
                postalCode: '75008',
                country: 'FR'
              });
            }}
          >
            🗼 Exemple Paris
          </button>
        </div>
      </div>

      <hr className="my-3" />

      <h6 className="mb-3"><AutoTranslateText sourceLang="fr">🎨 Paramètres d'affichage</AutoTranslateText></h6>
      
      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Taille de la carte</AutoTranslateText></label>
        <select
          className="form-select"
          value={config.mapSize || 'medium'}
          onChange={(e) => updateConfig({ mapSize: e.target.value })}
        >
          <option value="small"><AutoTranslateText sourceLang="fr">Petite (300x200px)</AutoTranslateText></option>
          <option value="medium"><AutoTranslateText sourceLang="fr">Moyenne (500x300px)</AutoTranslateText></option>
          <option value="large"><AutoTranslateText sourceLang="fr">Grande (700x400px)</AutoTranslateText></option>
          <option value="xl"><AutoTranslateText sourceLang="fr">Très grande (900x500px)</AutoTranslateText></option>
          <option value="custom"><AutoTranslateText sourceLang="fr">Personnalisée</AutoTranslateText></option>
        </select>
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={config.responsive || false}
          onChange={(e) => updateConfig({ responsive: e.target.checked })}
        />
        <label className="form-check-label">
          Mode responsive (largeur 100%)
        </label>
      </div>

      {config.mapSize === 'custom' && (
        <div className="row">
          <div className="col-md-6">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Largeur (px)</AutoTranslateText></label>
            <input
              type="number"
              className="form-control"
              value={config.customWidth || 500}
              onChange={(e) => updateConfig({ customWidth: parseInt(e.target.value) })}
              min="200"
              max="1200"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Hauteur (px)</AutoTranslateText></label>
            <input
              type="number"
              className="form-control"
              value={config.customHeight || 300}
              onChange={(e) => updateConfig({ customHeight: parseInt(e.target.value) })}
              min="150"
              max="800"
            />
          </div>
        </div>
      )}
      
      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Niveau de zoom (1-20)</AutoTranslateText></label>
        <input
          type="range"
          className="form-range"
          min="1"
          max="20"
          value={config.zoom || 15}
          onChange={(e) => updateConfig({ zoom: parseInt(e.target.value) })}
        />
        <small className="text-muted">Zoom: {config.zoom || 15}</small>
      </div>
      
      <div className="mb-3">
        <label className="form-label"><AutoTranslateText sourceLang="fr">Style de carte</AutoTranslateText></label>
        <select
          className="form-select"
          value={config.mapType || 'roadmap'}
          onChange={(e) => updateConfig({ mapType: e.target.value })}
        >
          <option value="roadmap"><AutoTranslateText sourceLang="fr">Plan (par défaut)</AutoTranslateText></option>
          <option value="satellite"><AutoTranslateText sourceLang="fr">Vue satellite</AutoTranslateText></option>
          <option value="hybrid"><AutoTranslateText sourceLang="fr">Hybride (satellite + labels)</AutoTranslateText></option>
          <option value="terrain"><AutoTranslateText sourceLang="fr">Relief et terrain</AutoTranslateText></option>
        </select>
      </div>
      
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={config.showMarker !== false}
          onChange={(e) => updateConfig({ showMarker: e.target.checked })}
        />
        <label className="form-check-label">
          Afficher le marqueur sur la carte
        </label>
      </div>

      {/* ✅ AJOUT DES CHAMPS DE STYLE ICI */}
      <div className="row mt-3">
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Couleur du texte</AutoTranslateText></label>
          <div className="d-flex align-items-center">
            <input
              type="color"
              className="form-control form-control-color"
              value={config.textColor || '#333333'}
              onChange={(e) => updateConfig({ textColor: e.target.value })}
              title="Choisir la couleur du texte"
            />
            <span className="ms-2 small text-muted">{config.textColor || '#333333'}</span>
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Arrondi des angles</AutoTranslateText></label>
          <div className="d-flex align-items-center">
             <input
              type="range"
              className="form-range flex-grow-1"
              min="0"
              max="20"
              step="1"
              value={config.borderRadius || 0}
              onChange={(e) => updateConfig({ borderRadius: parseInt(e.target.value) })}
            />
            <span className="ms-2 badge bg-light text-dark border">
              {config.borderRadius || 0}px
            </span>
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <h6 className="mb-3"><AutoTranslateText sourceLang="fr">🎯 Marqueurs multiples (NOUVEAU)</AutoTranslateText></h6>
      
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={config.multiMarkers || false}
          onChange={(e) => {
            updateConfig({ 
              multiMarkers: e.target.checked,
              markers: e.target.checked ? (config.markers || []) : []
            });
          }}
        />
        <label className="form-check-label">
          Activer les marqueurs multiples
        </label>
      </div>

      {config.multiMarkers && (
        <div className="border rounded p-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">Marqueurs ({(config.markers || []).length})</small>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const newMarker = {
                  id: `marker_${Date.now()}`,
                  title: `Marqueur ${(config.markers || []).length + 1}`,
                  latitude: config.latitude || 48.8566,
                  longitude: config.longitude || 2.3522,
                  description: '',
                  icon: 'default',
                  color: '#dc3545',
                  category: 'primary'
                };
                updateConfig({ 
                  markers: [...(config.markers || []), newMarker] 
                });
              }}
            >
              + Ajouter marqueur
            </button>
          </div>

          {(config.markers || []).map((marker: any, index: number) => (
            <div key={marker.id} className="card mb-2">
              <div className="card-body p-2">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={marker.title}
                    onChange={(e) => {
                      const updatedMarkers = [...config.markers];
                      updatedMarkers[index] = { ...marker, title: e.target.value };
                      updateConfig({ markers: updatedMarkers });
                    }}
                    placeholder="Titre du marqueur"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={() => {
                      const updatedMarkers = config.markers.filter((_: any, i: number) => i !== index);
                      updateConfig({ markers: updatedMarkers });
                    }}
                  >
                    🗑️
                  </button>
                </div>
                
                <div className="row">
                  <div className="col-6">
                    <input
                      type="number"
                      step="0.000001"
                      className="form-control form-control-sm"
                      value={marker.latitude}
                      onChange={(e) => {
                        const updatedMarkers = [...config.markers];
                        updatedMarkers[index] = { ...marker, latitude: parseFloat(e.target.value) };
                        updateConfig({ markers: updatedMarkers });
                      }}
                      placeholder="Latitude"
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="number"
                      step="0.000001"
                      className="form-control form-control-sm"
                      value={marker.longitude}
                      onChange={(e) => {
                        const updatedMarkers = [...config.markers];
                        updatedMarkers[index] = { ...marker, longitude: parseFloat(e.target.value) };
                        updateConfig({ markers: updatedMarkers });
                      }}
                      placeholder="Longitude"
                    />
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-6">
                    <select
                      className="form-select form-select-sm"
                      value={marker.category}
                      onChange={(e) => {
                        const updatedMarkers = [...config.markers];
                        updatedMarkers[index] = { ...marker, category: e.target.value };
                        updateConfig({ markers: updatedMarkers });
                      }}
                    >
                      <option value="primary"><AutoTranslateText sourceLang="fr">Principal</AutoTranslateText></option>
                      <option value="restaurant"><AutoTranslateText sourceLang="fr">Restaurant</AutoTranslateText></option>
                      <option value="hotel"><AutoTranslateText sourceLang="fr">Hôtel</AutoTranslateText></option>
                      <option value="poi"><AutoTranslateText sourceLang="fr">Point d'intérêt</AutoTranslateText></option>
                    </select>
                  </div>
                  <div className="col-6">
                    <input
                      type="color"
                      className="form-control form-control-sm"
                      value={marker.color}
                      onChange={(e) => {
                        const updatedMarkers = [...config.markers];
                        updatedMarkers[index] = { ...marker, color: e.target.value };
                        updateConfig({ markers: updatedMarkers });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="row mt-3">
            <div className="col-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={config.showRoute || false}
                  onChange={(e) => updateConfig({ showRoute: e.target.checked })}
                />
                <label className="form-check-label">
                  Afficher itinéraire
                </label>
              </div>
            </div>
            <div className="col-6">
              <select
                className="form-select form-select-sm"
                value={config.routeMode || 'driving'}
                onChange={(e) => updateConfig({ routeMode: e.target.value })}
                disabled={!config.showRoute}
              >
                <option value="driving"><AutoTranslateText sourceLang="fr">🚗 Voiture</AutoTranslateText></option>
                <option value="walking"><AutoTranslateText sourceLang="fr">🚶 À pied</AutoTranslateText></option>
                <option value="cycling"><AutoTranslateText sourceLang="fr">🚴 Vélo</AutoTranslateText></option>
                <option value="transit"><AutoTranslateText sourceLang="fr">🚌 Transport</AutoTranslateText></option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3">
        <h6 className="mb-2"><AutoTranslateText sourceLang="fr">🧭 Guidage</AutoTranslateText></h6>
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={config.showUserDirections || false}
            onChange={(e) => updateConfig({ showUserDirections: e.target.checked })}
          />
          <label className="form-check-label">
            Activer le guidage (position utilisateur → point principal)
          </label>
        </div>
        <select
          className="form-select form-select-sm"
          value={config.directionsMode || 'driving'}
          onChange={(e) => updateConfig({ directionsMode: e.target.value })}
          disabled={!config.showUserDirections}
        >
          <option value="driving"><AutoTranslateText sourceLang="fr">🚗 Voiture</AutoTranslateText></option>
          <option value="walking"><AutoTranslateText sourceLang="fr">🚶 À pied</AutoTranslateText></option>
          <option value="cycling"><AutoTranslateText sourceLang="fr">🚴 Vélo</AutoTranslateText></option>
          <option value="transit"><AutoTranslateText sourceLang="fr">🚌 Transport</AutoTranslateText></option>
        </select>
      </div>
    </div>
  );
};

// Composant de prévisualisation
const GeolocationPreviewComponent: React.FC<PluginPreviewProps> = ({ config, onConfigChange }) => {
  const { apiKey, isLoading } = useGoogleMapsApiKey();
  const isEditor = typeof onConfigChange === 'function';
  const [resolvedApiKey, setResolvedApiKey] = useState<string | null>(null);
  const [isResolvingKey, setIsResolvingKey] = useState(false);

  // ✅ Runtime: recuperer la cle via backend a partir du userId stocke
  useEffect(() => {
    let isMounted = true;
    const resolveKey = async () => {
      if (isEditor) {
        setResolvedApiKey(apiKey || null);
        return;
      }
      if (config.userId) {
        setIsResolvingKey(true);
        try {
          const keyValue = await apiKeyService.getGoogleMapsKeyByUserId(config.userId);
          if (isMounted) setResolvedApiKey(keyValue);
        } catch {
          if (isMounted) setResolvedApiKey(null);
        } finally {
          if (isMounted) setIsResolvingKey(false);
        }
        return;
      }
      if (config.googleMapsApiKeyId) {
        setIsResolvingKey(true);
        apiKeyService.getApiKeyValueById(config.googleMapsApiKeyId)
          .then((keyValue) => {
            if (isMounted) setResolvedApiKey(keyValue);
          })
          .catch(() => {
            if (isMounted) setResolvedApiKey(null);
          })
          .finally(() => {
            if (isMounted) setIsResolvingKey(false);
          });
        return;
      }
      setResolvedApiKey(null);
    };

    resolveKey();
    return () => { isMounted = false; };
  }, [isEditor, apiKey, config.userId, config.googleMapsApiKeyId]);

  const apiKeyToUse = resolvedApiKey || (isEditor ? apiKey : null);

  const [previewCoords, setPreviewCoords] = useState<{lat: number, lng: number} | null>(null);
  const [isLoadingCoords, setIsLoadingCoords] = useState(false);
  const [previewMapType, setPreviewMapType] = useState<'roadmap' | 'satellite' | 'hybrid' | 'terrain'>('roadmap');
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [userDirectionsEnabled, setUserDirectionsEnabled] = useState(!!config.showUserDirections);
  const isEditorMode = typeof onConfigChange === 'function';
  const [geoRequestKey, setGeoRequestKey] = useState(0);

  // Si l'admin désactive dans le setup, on force à false
  useEffect(() => {
    if (!config.showUserDirections) {
      setUserDirectionsEnabled(false);
    }
  }, [config.showUserDirections]);

  // Fonction pour mettre à jour la configuration avec les nouvelles coordonnées
  const updateConfigFromPreview = (lat: number, lng: number) => {
    if (onConfigChange) {
      onConfigChange({ 
        ...config, 
        latitude: lat, 
        longitude: lng 
      });
    }
  };

  // Fonction pour obtenir les coordonnées depuis l'adresse
  const getCoordinatesFromAddress = async () => {
    if (!apiKeyToUse || isLoadingCoords) return;

    await loadGoogleMapsScript(apiKeyToUse);

    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      return;
    }

    const fullAddress = [config.address, config.city, config.postalCode, countries.find(c => c.code === config.country)?.name]
      .filter(Boolean)
      .join(', ');

    if (!fullAddress.trim()) return;

    setIsLoadingCoords(true);
    try {
      const geocoder: GoogleMapsGeocoder = new window.google.maps.Geocoder();
      const result = await new Promise<any[]>((resolve, reject) => {
        geocoder.geocode({ address: fullAddress }, (results: any[], status: string) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Géocodage échoué: ${status}`));
          }
        });
      });

      const location: GoogleMapsLocation = result[0].geometry.location;
      setPreviewCoords({
        lat: location.lat(),
        lng: location.lng()
      });
    } catch (error) {
      console.error('Erreur de géocodage pour la préview:', error);
    } finally {
      setIsLoadingCoords(false);
    }
  };

  // Utiliser les coordonnées configurées ou essayer de les obtenir depuis l'adresse
  useEffect(() => {
    if (config.latitude && config.longitude) {
      setPreviewCoords({ lat: config.latitude, lng: config.longitude });
    } else if (apiKeyToUse && (config.address || config.city)) {
      getCoordinatesFromAddress();
    } else {
      setPreviewCoords(null);
    }
  }, [config.latitude, config.longitude, config.address, config.city, config.postalCode, config.country, apiKeyToUse]);

  useEffect((): ObserverCleanup | void => {
    const observer = createSafeIntersectionObserver(() => {}, { threshold: 0.1 });
    const target = previewRef.current;

    observeIfElement(observer, target);

    return () => {
      unobserveIfElement(observer, target);
    };
  }, []);

  if ((isEditor && isLoading) || isResolvingKey || isLoadingCoords) {
    return (
      <div className="text-center text-muted">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden"><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></span>
        </div>
        <p className="small mt-2">
          {isResolvingKey || (isEditor && isLoading) ? 'Chargement de la cle API...' : 'Recherche des coordonnees...'}
        </p>
      </div>
    );
  }

  if (!apiKeyToUse) {
    return (
      <div className="text-center text-muted">
        <div className="mb-2">
          <span className="badge bg-warning fs-4 p-3">⚠️</span>
        </div>
        <h6>{config.title || 'Carte de localisation'}</h6>
        <p className="small text-warning"><AutoTranslateText sourceLang="fr">Clé API Google Maps requise</AutoTranslateText></p>
      </div>
    );
  }

  if (!previewCoords) {
    return (
      <div className="text-center text-muted">
        <div className="mb-2">
          <span className="badge bg-info fs-4 p-3">🗺️</span>
        </div>
        <h6>{config.title || 'Carte de localisation'}</h6>
        <p className="small"><AutoTranslateText sourceLang="fr">Saisissez une adresse ou des coordonnées pour voir la carte</AutoTranslateText></p>
        <div className="mt-3">
          <small className="text-info">
            💡 Ajoutez une adresse dans la configuration pour générer automatiquement la carte
          </small>
        </div>
      </div>
    );
  }

  // Calculer les dimensions
  let width = 500, height = 300;
  switch (config.mapSize) {
    case 'small': width = 300; height = 200; break;
    case 'medium': width = 500; height = 300; break;
    case 'large': width = 700; height = 400; break;
    case 'xl': width = 900; height = 500; break;
    case 'custom': width = config.customWidth || 500; height = config.customHeight || 300; break;
  }

  // ✅ APPLICATION DU BORDER RADIUS AU STYLE
  const mapStyle = config.responsive
    ? { 
        width: '100%', 
        height: `${height}px`,
        overflow: 'hidden'
      }
    : { 
        width: `${width}px`, 
        height: `${height}px`,
        overflow: 'hidden'
      };

  const additionalMarkers = (config.multiMarkers ? (config.markers || []) : [])
    .map((m: any, i: number) => ({
      id: m.id || `marker_${i}`,
      latitude: Number(m.latitude),
      longitude: Number(m.longitude),
      title: m.title || `Marqueur ${i + 1}`,
      description: m.description,
      color: m.color,
      icon: m.icon,
    }))
    .filter((m: any) => Number.isFinite(m.latitude) && Number.isFinite(m.longitude));

  const fullAddress = [config.address, config.city, config.postalCode].filter(Boolean).join(', ');

  return (
    // ✅ APPLICATION DE LA COULEUR DE TEXTE ET DE L'ARRONDI AU CADRE GÉNÉRAL
    <div className="text-center" ref={previewRef} style={{ 
      color: config.textColor || '#333333',
      borderRadius: `${config.borderRadius || 0}px`,
      overflow: 'hidden'
    }}>
      <div className="mb-3">
        {/* On force l'héritage de couleur */}
        <h6 className="mb-2" style={{ color: 'inherit' }}>{config.title || 'Carte de localisation'}</h6>
        {fullAddress && (
          <p className="small mb-1" style={{ opacity: 0.8 }}>{fullAddress}</p>
        )}
        
        {/* Contrôles de la prévisualisation */}
        <div className="d-flex justify-content-center gap-2 mt-2 mb-3">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${previewMapType === 'roadmap' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPreviewMapType('roadmap')}
              title="Vue plan"
            >
              🗺️ Plan
            </button>
            <button
              type="button"
              className={`btn ${previewMapType === 'satellite' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setPreviewMapType('satellite')}
              title="Vue satellite"
            >
              🛰️ Satellite
            </button>
          </div>

          {config.showUserDirections && (
            <button
              type="button"
              className={`btn btn-sm ${userDirectionsEnabled ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => {
                setUserDirectionsEnabled((v) => {
                  const next = !v;
                  if (next) setGeoRequestKey((k) => k + 1);
                  return next;
                });
              }}
            >
              {userDirectionsEnabled ? '🧭 Guidage' : '🗺️ Carte'}
            </button>
          )}
        </div>
        
        {previewCoords && isEditorMode && (
          <div className="small text-info mb-2">
            <i className="fas fa-info-circle me-1"></i>
            {isDraggingMarker
              ? "Déplacez le marqueur pour ajuster la position"
              : "Cliquez et déplacez le marqueur pour ajuster la position"
            }
          </div>
        )}
      </div>
      
      <div className="d-flex justify-content-center">
        <div className="position-relative w-100" style={{ maxWidth: config.responsive ? '100%' : `${width}px` }}>
          <GoogleMap
            latitude={previewCoords.lat}
            longitude={previewCoords.lng}
            zoom={config.zoom || 15}
            width={width}
            height={height}
            mapType={previewMapType}
            showMarker={config.showMarker !== false}
            markerTitle={config.title || 'Point d\'intérêt'}
            address={fullAddress}
            apiKey={apiKeyToUse}
            mapId={config.mapId}
            className="mx-auto"
            style={mapStyle}
            additionalMarkers={additionalMarkers}
            onMarkerDragEnd={(lat, lng) => {
              setPreviewCoords({ lat, lng });
              updateConfigFromPreview(lat, lng);
              setIsDraggingMarker(false);
            }}
            onMarkerDragStart={() => setIsDraggingMarker(true)}
            draggableMarker={isEditorMode}
            showDragHint={isEditorMode}
            showDirections={userDirectionsEnabled}
            directionsMode={config.directionsMode}
            geolocationRequestKey={geoRequestKey}
          />
          
          {/* Overlay avec coordonnées en temps réel */}
          {previewCoords && (
            <div className="position-absolute top-0 end-0 m-1">
              <div className="badge bg-dark bg-opacity-75 text-light small">
                <div>Lat: {previewCoords.lat.toFixed(6)}</div>
                <div>Lng: {previewCoords.lng.toFixed(6)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-2">
        <small className="text-muted">
          Taille finale: {width}×{height}px
        </small>
        
        {previewCoords && config.latitude && config.longitude && 
         (Math.abs(previewCoords.lat - config.latitude) > 0.000001 || 
          Math.abs(previewCoords.lng - config.longitude) > 0.000001) && (
          <div className="mt-2">
            <div className="alert alert-info alert-sm py-1 px-2 small">
              <i className="fas fa-sync-alt me-1"></i>
              Position mise à jour automatiquement
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Fonction pour générer le HTML
const generateGeolocationHTML = (config: any): string => {
  // ✅ 1. Génération de l'ID unique
  const instanceId = config.id || `geo-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  
  // ✅ 2. On sauvegarde l'ID dans la config
  const configWithId = { ...config, id: instanceId };

  const fullAddress = [config.address, config.city, config.postalCode, countries.find(c => c.code === config.country)?.name]
    .filter(Boolean)
    .join(', ');
    
  const description = fullAddress || `📍 Latitude: ${config.latitude}, Longitude: ${config.longitude}`;
  
  return generateUniformPlaceholder({
    // ✅ C'est ici que ça se joue : ID unique pour le bouton "Éditer"
    pluginId: instanceId,
    pluginType: 'geolocation', // Doit correspondre à l'ID dans metadata

    title: config.title || 'Carte de localisation',
    description: description,
    icon: '🗺️',
    category: 'media',
    
    // ✅ On passe la config enrichie avec l'ID
    config: configWithId
  });
};

// Définition du plugin
const GeolocationPluginModular: PluginInterface = {
  metadata: {
    id: 'geolocation',
    name: 'Géolocalisation',
    description: 'Affichage de cartes interactives avec marqueurs de localisation',
    version: '1.0.0',
    author: 'Book Editor Team',
    category: 'media',
    icon: '🗺️',
    tags: ['carte', 'géolocalisation', 'map', 'coordonnées']
  },
  
  // ✅ DOCUMENTATION ENRICHIE POUR L'IA (ContainerWizard)
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Affiche une carte Google Maps interactive. Gère le géocodage d'adresse, les marqueurs et le calcul d'itinéraire (guidage).",
    usage: "Utilise ce plugin quand l'utilisateur demande une 'carte', un 'plan', une 'localisation', 'itinéraire' ou veut montrer une adresse.",
    
    configSchema: {
      type: "object",
      properties: {
        title: { type: "string", description: "Titre visible au-dessus de la carte" },
        
        // Localisation
        address: { type: "string", description: "Adresse précise (Rue, Numéro) pour le géocodage automatique" },
        city: { type: "string" },
        postalCode: { type: "string" },
        country: { type: "string", description: "Code ISO 2 lettres (FR, US, GP...)" },
        
        // Vue
        zoom: { type: "number", description: "1 (Monde) à 20 (Batiment). Défaut: 15" },
        mapType: { type: "string", enum: ["roadmap", "satellite", "hybrid", "terrain"] },
        
        // Fonctionnalités
        showMarker: { type: "boolean", default: true },
        showUserDirections: { type: "boolean", description: "Active le bouton 'Guidage/Itinéraire' depuis la position du visiteur" },
        directionsMode: { type: "string", enum: ["driving", "walking", "cycling", "transit"], description: "Mode de transport par défaut pour le guidage" },
        
        // Style
        responsive: { type: "boolean", default: true, description: "Prend 100% de la largeur disponible" },
        mapSize: { type: "string", enum: ["small", "medium", "large", "xl"] },
        borderRadius: { type: "number", description: "Arrondi des bords (0-20)" },
        textColor: { type: "string (hex)" }
      }
    } as any,

    exampleConfig: {
      title: "Rendez-vous au siège",
      address: "1 Place de la République",
      city: "Paris",
      country: "FR",
      zoom: 16,
      mapType: "roadmap",
      showMarker: true,
      showUserDirections: true, // Exemple avec guidage activé
      directionsMode: "transit",
      responsive: true,
      borderRadius: 12
    }
  },

  ConfigComponent: GeolocationConfigComponent,
  PreviewComponent: GeolocationPreviewComponent,
  generateHTML: generateGeolocationHTML, // Utilisation de la fonction corrigée
  defaultConfig: defaultGeolocationConfig,
  // Fonction de validation pour empêcher l'insertion sans clé API
  canInsert: async () => {
    try {
      const apiKeys = await apiKeyService.getApiKeys();
      
      // Recherche avec différentes variantes possibles
      const googleMapsKey = apiKeys.find((key: any) => {
        // Vérifier plusieurs variantes possibles
        const isGoogleProvider = key.provider === 'google';
        const isMapService = key.service === 'maps' || 
                           key.service === 'Google Maps' || 
                           key.service.toLowerCase().includes('maps');
        
        return isGoogleProvider && isMapService;
      });
      
      return !!googleMapsKey;
    } catch (error) {
      console.error('Erreur lors de la vérification de la clé API:', error);
      return false;
    }
  }
};

export default GeolocationPluginModular;

type ObserverCleanup = () => void;

const isElement = (value: unknown): value is Element =>
  !!value && typeof value === 'object' && (value as Element).nodeType === 1;

const createSafeIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): IntersectionObserver | null => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null;
  return new IntersectionObserver(callback, options);
};

const observeIfElement = (observer: IntersectionObserver | null, target: unknown) => {
  if (!observer || !isElement(target)) return;
  observer.observe(target);
};

const unobserveIfElement = (observer: IntersectionObserver | null, target: unknown) => {
  if (!observer || !isElement(target)) return;
  observer.unobserve(target);
  observer.disconnect();
};

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return loadGoogleMapsApi(apiKey);
};
